// Axios adapter that resolves every request from local mock data when
// VITE_DEMO_MODE is on. Single point of swap so we don't have to touch
// any of the 50+ api.* methods in src/lib/api.ts.
//
// Two payload shapes exist in the real backend and the api.ts layer
// reads both. We label each matcher with which shape it should return:
//
//   - 'raw'      → matcher returns the data the page sees directly
//                  (used by methods that just `return response.data`).
//   - 'envelope' → matcher returns `{ success: true, data: <value> }`
//                  (used by methods that `return response.data.data`).
//
// For URLs not matched explicitly we return a "soft success" payload so
// pages that still call unmocked endpoints render an empty-state cleanly
// rather than throwing.

import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import {
  demoActivityFeed,
  demoBaselineDrifts,
  demoComplianceDashboard,
  demoCurrentUser,
  demoComplianceScores,
  demoDashboardStats,
  demoDriftTrends,
  demoExecutiveReport,
  demoLicenseSkus,
  demoOAuthApps,
  demoOAuthAppsSummary,
  demoPermissionHeatmap,
  demoPhishMfa,
  demoPriorityActions,
  demoRecentActivity,
  demoRecentConsents,
  demoRecentScanJobs,
  demoScubaSummary,
  demoSecureScoreRecommendations,
  demoSecureScoreSummary,
  demoTenant,
  demoTenants,
  demoTenantStandards,
  demoUserInsights,
  NOW,
} from './mockData'

type Shape = 'raw' | 'envelope'

interface Match {
  test: (path: string, method: string) => boolean
  shape: Shape
  data: () => unknown
}

const matchers: Match[] = [
  // Identity
  { test: (p, m) => m === 'get' && /\/usermanagement\/me\b/.test(p),
    shape: 'raw', data: () => demoCurrentUser },

  // Tenants — note: api.ts uses `/tenants` and `/tenants/${id}` (envelope).
  { test: (p, m) => m === 'get' && /^\/tenants$/.test(p),
    shape: 'envelope', data: () => demoTenants },
  { test: (p, m) => m === 'get' && /^\/tenants\/[^/]+$/.test(p),
    shape: 'envelope', data: () => demoTenant },
  { test: (p, m) => m === 'get' && /^\/tenantmanagement\b/.test(p),
    shape: 'raw', data: () => demoTenants },

  // Dashboard / compliance — api.ts reads response.data directly.
  { test: (p, m) => m === 'get' && /^\/dashboard\/tenant\/[^/]+$/.test(p),
    shape: 'raw', data: () => demoComplianceDashboard() },
  { test: (p, m) => m === 'get' && /\/dashboard\/.+\/recent-activity/.test(p),
    shape: 'raw', data: () => demoRecentActivity() },
  { test: (p, m) => m === 'get' && /\/dashboard\/.+\/drift-trends/.test(p),
    shape: 'raw', data: () => demoDriftTrends() },
  { test: (p, m) => m === 'get' && /\/dashboard\/.+\/stats/.test(p),
    shape: 'raw', data: () => demoDashboardStats() },
  { test: (p, m) => m === 'get' && /\/dashboard\/frameworks/.test(p),
    shape: 'envelope', data: () => [] },
  { test: (p, m) => m === 'get' && /\/dashboard\/tenant\/[^/]+\/baseline\/exists/.test(p),
    shape: 'envelope', data: () => true },
  { test: (p, m) => m === 'get' && /\/dashboard\/tenant\/[^/]+\/baseline/.test(p),
    shape: 'envelope', data: () => ({ tenantId: demoTenant.tenantId, exists: true }) },

  // Configuration drift list — page expects { items, total, page, limit }
  { test: (p, m) => m === 'get' && /^\/configurationdrift\/[^/]+\/audit-history/.test(p),
    shape: 'envelope', data: () => [] },
  { test: (p, m) => m === 'get' && /^\/configurationdrift\/[^/]+(\?|$)/.test(p),
    shape: 'envelope', data: () => demoBaselineDrifts()[0] },
  { test: (p, m) => m === 'get' && /^\/configurationdrift(\?|$)/.test(p),
    shape: 'envelope', data: () => {
      const items = demoBaselineDrifts()
      return { items, total: items.length, page: 1, limit: 50 }
    } },

  // Scan jobs — raw shape
  { test: (p, m) => m === 'get' && /\/scan\/tenant\/[^/]+\/jobs/.test(p),
    shape: 'raw', data: () => demoRecentScanJobs() },
  { test: (p, m) => m === 'get' && /\/scan\/tenant\/[^/]+\/limits/.test(p),
    shape: 'raw', data: () => ({ allowed: true, dailyLimit: 24, used: 2 }) },
  { test: (p, m) => m === 'get' && /\/scan\/job\/[^/]+/.test(p),
    shape: 'raw', data: () => ({ ...demoRecentScanJobs()[0], status: 'completed' }) },
  { test: (p, m) => m === 'get' && /\/scan\/admin\/errors/.test(p),
    shape: 'raw', data: () => [] },

  // Secure score
  { test: (p, m) => m === 'get' && /\/securescore\/.+\/summary/.test(p),
    shape: 'raw', data: () => demoSecureScoreSummary() },
  { test: (p, m) => m === 'get' && /\/securescore\b/.test(p),
    shape: 'raw', data: () => demoSecureScoreSummary() },

  // User insights — split specifically so /licenses returns the SKU
  // array shape rather than the summary object.
  { test: (p, m) => m === 'get' && /\/userinsights\/.+\/phish-resistant-mfa/.test(p),
    shape: 'raw', data: () => demoPhishMfa() },
  { test: (p, m) => m === 'get' && /\/userinsights\/.+\/licenses(\?|$)/.test(p),
    shape: 'raw', data: () => demoLicenseSkus() },
  { test: (p, m) => m === 'get' && /\/userinsights\/.+\/(summary|status|settings|trends|users)/.test(p),
    shape: 'raw', data: () => demoUserInsights() },
  { test: (p, m) => m === 'get' && /\/userinsights\b/.test(p),
    shape: 'raw', data: () => demoUserInsights() },

  // OAuth apps
  { test: (p, m) => m === 'get' && /\/oauth-apps\/tenant\/[^/]+\/summary/.test(p),
    shape: 'raw', data: () => demoOAuthAppsSummary() },
  { test: (p, m) => m === 'get' && /\/oauth-apps\/tenant\/[^/]+\/[^/]+/.test(p),
    shape: 'raw', data: () => demoOAuthApps().apps[0] },
  { test: (p, m) => m === 'get' && /\/oauth-apps\/tenant\/[^/]+/.test(p),
    shape: 'raw', data: () => demoOAuthApps() },

  // SCuBA
  { test: (p, m) => m === 'get' && /\/scuba\/.+\/summary/.test(p),
    shape: 'raw', data: () => demoScubaSummary() },
  { test: (p, m) => m === 'get' && /\/scuba\/.+\/risks/.test(p),
    shape: 'raw', data: () => ({ risks: [] }) },
  { test: (p, m) => m === 'get' && /\/scuba\b/.test(p),
    shape: 'raw', data: () => ({ baselines: demoScubaSummary().baselines, generatedAt: NOW() }) },

  // Compliance — order matters: specific subpaths before the generic
  // /compliance fallback that returns the scores object.
  { test: (p, m) => m === 'get' && /\/compliance\/tenant\/[^/]+\/standards(\?|$)/.test(p),
    shape: 'envelope', data: () => demoTenantStandards() },
  { test: (p, m) => m === 'get' && /\/compliance\/tenant\/[^/]+\/(evaluations|waivers)/.test(p),
    shape: 'envelope', data: () => [] },
  { test: (p, m) => m === 'get' && /\/compliance\/standards/.test(p),
    shape: 'envelope', data: () => [] },
  { test: (p, m) => m === 'get' && /\/compliance\/.+\/scores/.test(p),
    shape: 'raw', data: () => demoComplianceScores() },
  { test: (p, m) => m === 'get' && /\/compliance\b/.test(p),
    shape: 'raw', data: () => demoComplianceScores() },
  { test: (p, m) => m === 'get' && /\/executive-report\/[^/]+\/data(\?|$)/.test(p),
    shape: 'raw', data: () => demoExecutiveReport() },
  // PDF/HTML download — return an empty blob-shaped payload so the
  // "Download PDF" button doesn't error in the demo.
  { test: (p, m) => m === 'get' && /\/executive-report\/[^/]+\/download/.test(p),
    shape: 'raw', data: () => '' },

  // Tenant status / queue — return idle-shaped responses
  { test: (p, m) => m === 'get' && /\/tenantstatus\/.+\/status/.test(p),
    shape: 'raw', data: () => ({ tenantId: demoTenant.tenantId, queuedJobs: 0, runningJobs: 0, recentJobs: demoRecentScanJobs() }) },
  { test: (p, m) => m === 'get' && /\/tenantstatus\b/.test(p),
    shape: 'raw', data: () => ({ totalQueued: 0, totalRunning: 0 }) },

  // Metric trends — used by some Dashboard widgets
  { test: (p, m) => m === 'get' && /\/metrictrends\/tenant\/[^/]+/.test(p),
    shape: 'raw', data: () => ({
      secureScore: { metricType: 'secureScore', currentValue: 76, previousWeekValue: 74, changeAmount: 2, changePercentage: 2.7, direction: 'up', history: [] },
      mfaAdoption: { metricType: 'mfa', currentValue: 94, previousWeekValue: 92, changeAmount: 2, changePercentage: 2.2, direction: 'up', history: [] },
      baselineCompliance: { metricType: 'baseline', currentValue: 84, previousWeekValue: 81, changeAmount: 3, changePercentage: 3.7, direction: 'up', history: [] },
      generatedAt: NOW(),
    }) },

  // Command Center additions (post-rebrand)
  // Priority actions queue — raw array shape, matches getPriorityActions().
  { test: (p, m) => m === 'get' && /\/posture\/priority-actions\/tenant\/[^/]+/.test(p),
    shape: 'raw', data: () => demoPriorityActions() },

  // Activity feed
  { test: (p, m) => m === 'get' && /\/activity\/tenant\/[^/]+/.test(p),
    shape: 'raw', data: () => demoActivityFeed() },

  // OAuth permission heatmap + recent consents
  { test: (p, m) => m === 'get' && /\/oauth-apps\/permission-heatmap/.test(p),
    shape: 'raw', data: () => demoPermissionHeatmap() },
  { test: (p, m) => m === 'get' && /\/oauth-apps\/recent-consents/.test(p),
    shape: 'raw', data: () => demoRecentConsents() },

  // Secure score recommendations
  { test: (p, m) => m === 'get' && /\/securescore\/tenant\/[^/]+\/recommendations/.test(p),
    shape: 'raw', data: () => demoSecureScoreRecommendations() },

  // OPA + DevOps integration status checks — return idle/ok
  { test: (p, m) => m === 'get' && /\/opa\/health/.test(p),
    shape: 'raw', data: () => ({ status: 'ok', version: 'demo', evaluatedAt: NOW() }) },
  { test: (p, m) => m === 'get' && /\/devopsintegration/.test(p),
    shape: 'raw', data: () => ({ configured: false }) },

  // Tenant consent flow — return "consented" status so demo UIs don't
  // surface "needs admin consent" banners.
  { test: (p, m) => m === 'get' && /\/tenant-consent\/[^/]+\/status/.test(p),
    shape: 'raw', data: () => ({ status: 'granted', grantedAt: NOW() }) },
]

function ok(data: unknown, config: InternalAxiosRequestConfig): AxiosResponse {
  return {
    data,
    status: 200,
    statusText: 'OK',
    headers: { 'content-type': 'application/json' },
    config,
    request: {},
  }
}

export const demoAdapter: AxiosAdapter = async (config) => {
  const path = (config.url || '').replace(/^https?:\/\/[^/]+/, '')
  const method = (config.method || 'get').toLowerCase()

  // Write methods: just succeed without persisting. Lets buttons + forms
  // in the demo "save" without throwing.
  if (method !== 'get' && method !== 'head') {
    return ok({ success: true, message: 'Saved (demo)', data: null }, config)
  }

  for (const m of matchers) {
    if (m.test(path, method)) {
      await new Promise((r) => setTimeout(r, 120))
      const value = m.data()
      return ok(m.shape === 'envelope' ? { success: true, data: value } : value, config)
    }
  }

  // Default: empty-but-valid envelope. Most pages render an empty state.
  return ok({ success: true, data: null, items: [], totalCount: 0 }, config)
}
